import { css } from '@emotion/react';
import { motion } from 'motion/react';
import {
  type FC, type PropsWithChildren, type ReactNode, useEffect,
} from 'react';
import { useDispatch } from 'react-redux';
import { RadioComponent } from '~/_shared/baseComponents/radio/radio.component';
import {
  TooltipBehavior, TooltipComponent,
} from '~/_shared/baseComponents/tooltip/tooltip.component';
import { type ColorResult } from '~/_shared/components/colorPicker/colorPicker.types';
import { LimitNumberOfLinesComponent } from '~/_shared/components/limitNumberOfLines/limitNumberOfLines.component';
import { useHighlightAnimation } from '~/_shared/hooks/useHighlightAnimation';
import { type Theme } from '~/_shared/themes/theme.model';
import { useTranslation } from '~/_shared/utils/hooks';
import {
  type ActiveBoundaryFillDropdownProps, ActiveBoundaryFillHeader,
} from '~/sidebar/sidebarApps/mapTools/boundary/activeBoundary/activeBoundaryFillIHeader';
import { boundaryTerritoryGroupInSidebarHighlightFinished } from '~/store/frontendState/mapTools/boundary/boundarySidebarState/boundarySidebarState.actionCreators';
import {
  actionsWrapperStyle, mainHeaderStyle, nameSubheaderStyle, primaryBoundaryLabelStyle,
  trialFlagContainerStyle,
} from './styles';
import { TrialModeWarningComponent } from './trialMode/trialModeWarning.component';

export type BoundaryTerritoryKeySettings = Readonly<{
  isCustom: boolean;
  isEmpty: boolean;
  isVisible: boolean;
  label: string;
  selectedColor: ColorResult;
  lineColor: ColorResult;
  lineWidth: number;

  onBatchEditLocationsClick?: () => void;
  onDelete?: () => void;
  onExportData?: () => void;
  onExportContainedBoundaries?: () => void;
  onToggle: () => void;
}>;

export type BoundaryKeySettings = Readonly<{
  isCustom: boolean;
  isVisible: boolean;
  label: string;
  selectedColor: ColorResult;
  lineColor: ColorResult;
  lineWidth: number;

  onBatchEditLocationsClick?: () => void;
  onBoundarySettingsClick?: () => void;
  onExportDataInBoundaryClick?: () => void;
  onExportContainedBoundaries?: () => void;
  onManuallyEditPolygonClick?: () => void;
  onRemoveBoundaryClick?: () => void;
  onToggle: () => void;
  onZoomToBoundaryClick: () => void;
}>;

export type ActiveBoundaryProps = Readonly<{
  activeExtraBoundaryFillId?: Uuid | null;
  boundaryIsOptimizedTerritoriesTrial: boolean;
  dropdownProps: ActiveBoundaryFillDropdownProps;
  isHighlighted?: boolean;
  isLegendMode?: boolean;
  isPrimary: boolean;
  name: string;
  showActions: boolean;
  toolbar: ReactNode;

  onToggleIsPrimary: () => void;
}>;

const wmsTrialStyle = (theme: Theme) => css({
  border: `2px solid ${theme.textColors.warning}`,
  borderRightWidth: '3px', // scrollbar eats 1px
});

const toolbarWrapperStyle = css({
  minHeight: 34,
});

export const ActiveBoundaryComponent: FC<PropsWithChildren<ActiveBoundaryProps>> = ({
  boundaryIsOptimizedTerritoriesTrial,
  children,
  dropdownProps,
  isHighlighted,
  isLegendMode,
  isPrimary,
  name,
  onToggleIsPrimary,
  showActions,
  toolbar,
}) => {
  const [t] = useTranslation();
  const dispatch = useDispatch();

  const {
    staticHighlightStyles,
    startHighlightAnimation,
    animationScopeRef,
    isAnimationActive,
  } = useHighlightAnimation();

  useEffect(() => {
    if (!isHighlighted || !animationScopeRef.current) {
      return;
    }
    if (!isAnimationActive) {
      animationScopeRef.current.scrollIntoView();
      startHighlightAnimation();
    }
    dispatch(boundaryTerritoryGroupInSidebarHighlightFinished());
  }, [isHighlighted, animationScopeRef, isAnimationActive, dispatch, startHighlightAnimation]);

  return (
    <motion.div
      ref={animationScopeRef}
      css={[staticHighlightStyles, boundaryIsOptimizedTerritoriesTrial && wmsTrialStyle]}
    >

      <ActiveBoundaryFillHeader
        dropdownProps={dropdownProps}
        isLegendMode={isLegendMode}
      />

      <div css={[nameSubheaderStyle, dropdownProps.isOnlyNofillOption ? mainHeaderStyle : undefined]}>
        <TooltipComponent
          behavior={TooltipBehavior.ShowOnOverflow}
          delayShow={500}
          tooltipContent={name}
          overflowTolerance={{ x: 0, y: 3 }}
        >
          <LimitNumberOfLinesComponent maxNumberOfLines={2}>
            {name}
          </LimitNumberOfLinesComponent>
        </TooltipComponent>
        {boundaryIsOptimizedTerritoriesTrial && (
          <div css={trialFlagContainerStyle}>
            <TrialModeWarningComponent />
          </div>
        )}
      </div>

      {showActions && (
        <div css={actionsWrapperStyle}>
          <div>
            <RadioComponent
              isChecked={isPrimary}
              onChange={onToggleIsPrimary}
              label={t('Active')}
              css={primaryBoundaryLabelStyle}
              value="active"
            />
          </div>

          <div css={toolbarWrapperStyle}>
            {toolbar}
          </div>
        </div>
      )}
      {children}
    </motion.div>
  );
};
